import { useCallback, useContext, useEffect, useRef } from 'react';
import { tap } from 'rxjs/operators';
import dlv from 'dlv';

import { createDebug } from '../utils/debug';
import { RxValidateFn } from '../types';
import { RxFormContext } from '../Context';

const debug = createDebug('useRxInternalField');

interface UseRxInternalFieldParams {
    field: string;
    validate?: RxValidateFn;
    mask?: (v: any) => any;
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    validateNotify?: string[];
    initialValue?: any;
    consumerOnChange?(...args: any[]): void;
    consumerOnBlur?(...args: any[]): void;
    consumerOnValueChange?(...args: any[]): void;
}

export function useRxInternalField(params: UseRxInternalFieldParams) {
    const {
        field,
        validate,
        validateOnChange,
        validateOnBlur,
        validateNotify,
        initialValue,
        mask,
        consumerOnBlur,
        consumerOnChange,
        consumerOnValueChange,
    } = params;
    const { next, initialValues, getSetStream, getValueStream } = useContext(RxFormContext);
    const formInitialValue = initialValue ?? dlv(initialValues, field);
    const mountRef = useRef(0);

    /**
     * A ref to track access to the mask.
     * This is done to prevent inline functions causing a re-render of this component
     */
    const maskRef = useRef(mask);
    const valueChangeRef = useRef(consumerOnValueChange);

    /**
     * On change handler
     */
    const onChange = useCallback(
        (value) => {
            const elem = ref.current;
            const mask = maskRef.current;
            const nextValue = mask ? mask(value) : value;
            const valueChanged = valueChangeRef.current;
            debug('onChange', field, value);
            debug('onChange next (maybe masked)', field, nextValue);
            next({ type: 'field-change', value: nextValue, field: field });
            valueChanged && valueChanged(nextValue, value);

            /**
             * Also manually set the value manually via the ref
             * if a mask was used
             */
            if (ref && elem && mask) {
                /**
                 * Try to save the cursor position before updating the input
                 */
                const cursorStart = elem.selectionStart;
                const cursorEnd = elem.selectionEnd;

                /**
                 * Now set the value on the element
                 */
                switch (elem.type) {
                    case 'select-one':
                    case 'select-multiple':
                    case 'checkbox': {
                        break;
                    }
                    default: {
                        elem.value = nextValue;
                    }
                }

                /**
                 *
                 */
                if (typeof cursorStart === 'number' && typeof cursorEnd === 'number') {
                    if (typeof elem.setSelectionRange === 'function') {
                        elem.setSelectionRange(cursorStart, cursorEnd);
                    }
                }
            }
        },
        [next, field, maskRef, valueChangeRef],
    );

    /**
     * On blur handler
     */
    const onInputBlur = useCallback(
        (e) => {
            if (validateOnBlur) {
                debug('onBlur', field);
                next({ type: 'field-blur', field: field });
            }
            consumerOnBlur && consumerOnBlur(e);
        },
        [validateOnBlur, consumerOnBlur, field, next],
    );

    /**
     * Handle initial mount
     */
    useEffect(() => {
        debug('++ mount', field);
        const index = mountRef.current;
        const rxvalidate = validate
            ? {
                  fn: validate,
                  validateOnChange,
                  validateOnBlur,
                  validateNotify,
              }
            : undefined;
        next({
            type: 'field-mount',
            initialValue,
            field: field,
            validate: rxvalidate,
            index,
        });
        mountRef.current += 1;
    }, [initialValue, next, field, validate, validateOnChange, validateOnBlur, validateNotify]);

    /**
     * Handle field un-mount
     */
    useEffect(() => {
        return () => next({ type: 'field-unmount', field: field });
    }, [next, field]);

    const ref = useRef<HTMLInputElement>();
    const onInputChange = useCallback(
        (e) => {
            const type = e.target.type;
            switch (type) {
                case 'checkbox': {
                    debug('onInputChange:checkbox', e.target.checked);
                    onChange(e.target.checked);
                    break;
                }
                default: {
                    const value = e.target.value;
                    debug('onInputChange', field, [value]);
                    onChange(value);
                }
            }
            consumerOnChange && consumerOnChange(e);
        },
        [consumerOnChange, field, onChange],
    );

    useEffect(() => {
        /**
         * A manually set the value via the ref
         */
        const setter = (value) => {
            if (ref && ref.current) {
                debug('ref.current', field, value);
                switch (ref.current.type) {
                    case 'checkbox': {
                        ref.current.checked = !!value;
                        break;
                    }
                    default: {
                        ref.current.value = value;
                    }
                }
            }
            onChange(value);
        };
        /**
         * Observer the setter stream and set the value manually
         */
        const sets = getSetStream(field)
            .pipe(
                tap((evt) => {
                    if (evt.type === 'set-field-value') {
                        setter(evt.value);
                    }
                }),
            )
            .subscribe();

        /**
         * This will handle the 'initialValue' for each field
         * We don't use reacts 'defaultValue' because if the form
         * has been built in a way that causes lots of re-renders, then it
         * wont work since the value will be overridden.
         */
        if (mountRef.current === 1 && formInitialValue) {
            debug('Setting initial value', field);
            setter(formInitialValue);
        }
        return () => sets.unsubscribe();
    }, [field, formInitialValue, getSetStream, onChange, ref]);

    return {
        onChange,
        onInputBlur,
        formInitialValue,
        getSetStream,
        onInputChange,
        ref,
        getValueStream: getValueStream,
        mountRef,
    };
}
